import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { SegmentPatients } from "@/components/SegmentPatients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Mail, MessageSquare, Megaphone, Send, Users, CheckCircle2, Eye, CalendarCheck, Zap, Clock, Play } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const TEMPLATES = [
  { name: "6-Month Hormone Optimization Check-in", channel: "email", audience: "Lapsed TRT patients",
    subject: "Time for your hormone optimization review",
    body: "Hi {{patient_first_name}}, it's been a while since your last visit. Optimal testosterone levels require regular monitoring. Let's get you back on track — reply or call to schedule your 6-month check-in." },
  { name: "Lab Work Due Reminder", channel: "sms", audience: "Patients with labs due",
    subject: "Lab work reminder",
    body: "Hi {{patient_first_name}}, your routine lab work is due. Staying on top of your labs keeps your treatment safe and effective. Book here: {{booking_link}}" },
  { name: "VIP Patient Wellness Assessment", channel: "email", audience: "High lifetime value, inactive 90+ days",
    subject: "A complimentary wellness assessment, on us",
    body: "Hi {{patient_first_name}}, as a valued patient of The Men's Clinic, we'd like to offer you a complimentary wellness assessment. Let's optimize your health for the year ahead." },
];

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-mono-metric text-lg font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
    </div>
  );
}

export default function RecallCampaigns() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(TEMPLATES[0]);

  const { data: campaigns = [] } = useQuery({ queryKey: ["campaigns"], queryFn: async () => (await api.get("/campaigns")).data });
  const { data: segData } = useQuery({ queryKey: ["segments"], queryFn: async () => (await api.get("/campaigns/segments")).data });
  const segments = segData?.segments || [];
  const { data: recall } = useQuery({ queryKey: ["recall-status"], queryFn: async () => (await api.get("/recall/status")).data });

  const runRecall = useMutation({
    mutationFn: async () => (await api.post("/recall/run")).data,
    onSuccess: (d) => {
      toast.success(d.enrolled > 0
        ? `Auto-recall enrolled ${d.enrolled} patients`
        : "No new patients eligible for recall right now");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["recall-status"] });
    },
  });

  const launch = useMutation({
    mutationFn: async () => (await api.post("/campaigns", form)).data,
    onSuccess: (d) => {
      toast.success(`Campaign "${d.name}" launched to ${d.sent_count} patients`);
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      setOpen(false);
    },
  });

  const preview = useMutation({
    mutationFn: async (id) => (await api.post(`/campaigns/${id}/send-preview`)).data,
    onSuccess: (d) => toast.success(d.message),
    onError: () => toast.error("Could not send preview email"),
  });

  return (
    <Layout title="Patient Recall & Reactivation" subtitle="Win back lapsed patients with targeted campaigns"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="launch-campaign-button" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Megaphone className="h-4 w-4 mr-2" /> Launch Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Launch Recall Campaign</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map((t) => (
                  <button key={t.name} data-testid={`template-${t.channel}`}
                    onClick={() => setForm(t)}
                    className={`text-left p-2.5 rounded-lg border text-xs transition-colors ${form.name === t.name ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    {t.channel === "email" ? <Mail className="h-3.5 w-3.5 mb-1 text-slate-500" /> : <MessageSquare className="h-3.5 w-3.5 mb-1 text-slate-500" />}
                    <div className="font-semibold text-slate-700 leading-tight line-clamp-2">{t.name}</div>
                  </button>
                ))}
              </div>
              <div className="space-y-1.5"><Label>Campaign name</Label>
                <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Audience segment</Label>
                <Input data-testid="campaign-audience-input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Subject</Label>
                <Input data-testid="campaign-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Message ({form.channel.toUpperCase()})</Label>
                <Textarea data-testid="campaign-body-input" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="campaign-send-button" disabled={launch.isPending} onClick={() => launch.mutate()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                <Send className="h-4 w-4 mr-2" /> {launch.isPending ? "Launching…" : "Broadcast Campaign"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Automation banner */}
      <div data-testid="recall-automation-banner"
        className="bg-slate-950 text-white rounded-xl p-5 shadow-sm mb-6 flex flex-wrap items-center gap-4">
        <span className="h-11 w-11 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0">
          <Zap className="h-5 w-5 text-cyan-400" />
        </span>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold">Automated Recall</h3>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">On</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
            <Clock className="h-3 w-3" /> {recall?.schedule || "Daily"} · contacts up to {recall?.daily_cap ?? 20} lapsed (180+ days) or overdue (90+ days) patients per day, most recent first
          </p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="font-mono-metric text-xl font-bold text-cyan-300">{recall?.eligible_now ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Eligible now</div>
          </div>
          <div className="text-center">
            <div data-testid="recall-daily-cap" className="font-mono-metric text-xl font-bold text-amber-300">{recall?.daily_cap ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Max per day</div>
          </div>
          <div className="text-center">
            <div className="font-mono-metric text-xl font-bold">{recall?.last_run?.enrolled ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Last run</div>
          </div>
          <Button data-testid="run-recall-now-button" onClick={() => runRecall.mutate()} disabled={runRecall.isPending}
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            <Play className={`h-4 w-4 mr-2 ${runRecall.isPending ? "animate-pulse" : ""}`} /> Run now
          </Button>
        </div>
      </div>

      {/* Segments */}
      <div data-testid="segment-total" className="flex items-baseline gap-2 mb-3">
        <span className="font-mono-metric text-xl font-bold text-slate-900">{(segData?.total_patients ?? 0).toLocaleString()}</span>
        <span className="text-sm text-slate-500">total patients in the CRM, grouped below by recall need</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {segments.map((s, i) => (
          <motion.div key={s.id} data-testid={`segment-card-${s.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col">
            <Users className="h-5 w-5 text-cyan-500 mb-2" />
            <div data-testid={`segment-count-${s.id}`} className="font-mono-metric text-2xl font-bold text-slate-900">{s.count.toLocaleString()}</div>
            <div className="text-xs font-semibold text-slate-600 mt-0.5">{s.label}</div>
            <div data-testid={`segment-desc-${s.id}`} className="text-[11px] text-slate-400 mt-1 leading-snug flex-1">{s.description}</div>
            <SegmentPatients seg={s} />
          </motion.div>
        ))}
      </div>

      <h3 className="font-heading font-bold text-slate-900 mb-3">Campaign Performance</h3>
      <div className="space-y-4">
        {campaigns.map((c) => {
          const openRate = c.delivered ? Math.round((c.opened / c.delivered) * 100) : 0;
          return (
            <div key={c.id} data-testid={`campaign-row-${c.id}`}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.channel === "email" ? "bg-blue-50 text-blue-700" : "bg-violet-50 text-violet-700"}`}>{c.channel}</span>
                    {c.automated && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 flex items-center gap-1"><Zap className="h-2.5 w-2.5" /> Auto</span>}
                    <h4 className="font-semibold text-slate-900">{c.name}</h4>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c.audience} · launched {fmtDate(c.created_at)}</p>
                </div>
                <Button data-testid={`campaign-preview-${c.id}`} variant="outline" size="sm"
                  disabled={preview.isPending} onClick={() => preview.mutate(c.id)}>
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Email me a preview
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 border-t border-slate-100 pt-4">
                <Stat label="Sent" value={c.sent_count} />
                <Stat label="Delivered" value={`${c.delivered ? Math.round((c.delivered / c.sent_count) * 100) : 0}%`} />
                <Stat label="Opened" value={`${openRate}%`} />
                <Stat label="Booked" value={c.booked} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-6 flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5" /> Patient broadcasts are simulated on mock EMR data. "Email me a preview" sends a real email to your inbox.
      </p>
    </Layout>
  );
}
